import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  SoftwareManagementExchangeCurrencyFragment,
  SoftwareManagementSoftwarePaymentFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import MoneyField from 'modules/core/components/Inputs/MoneyField'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import CurrencySelector from 'modules/settings/components/CurrencySelector'
import { useUpdateSoftwarePayment } from 'modules/software_management/hooks/useUpdateSoftwarePayment'
import { useApplicationsContext } from 'modules/software_management/pages/ApplicationsPage'

import SoftwarePaymentForm from 'modules/software_management/components/ApplicationDetailsContent/PaymentsWidget/SoftwarePaymentForm'

interface Props {
  payment: SoftwareManagementSoftwarePaymentFragment
  softwareSetupId: string
  onSuccess: () => void
  onClose: () => void
  currencies: SoftwareManagementExchangeCurrencyFragment[]
}

const EditPaymentModal = ({ payment, softwareSetupId, onSuccess, onClose, currencies }: Props) => {
  const { currency: userCurrency } = useApplicationsContext()
  const { company } = useCurrentSession()

  const { update: updateSoftwarePayment, loading: updating } = useUpdateSoftwarePayment()

  const form = useLazyRef(
    () =>
      new SoftwarePaymentForm({
        amount: payment.amountCents,
        currency: payment.currency.toUpperCase(),
        date: dateToString(moment(payment.executedAt), 'backend'),
      })
  ).current

  const allowedCurrencies = currencies.map(({ id }) => id)

  const onSubmit = async () => {
    const { amount, currency, date } = form.data()

    const newDate = dateToString(stringToDate(date, 'backend'), 'backend_datetime')

    const result = await updateSoftwarePayment(
      {
        paymentId: payment.id,
        amount,
        currency,
        date: newDate,
      },
      userCurrency,
      company.id,
      softwareSetupId
    )

    const handled = handleResult(result, (formErrors) => {
      app.setFlash({
        type: 'wrong',
        message: formErrors.messages || i18n.t('errors.generic_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('software_management.software_setups.edit_payment_modal.success'),
      })
      onSuccess()
    }
  }

  return (
    <Modal
      illustration="money-bag"
      title={i18n.t('software_management.software_setups.edit_payment_modal.title')}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={updating || !form.isComplete} submit>
            {i18n.t('common.submit')}
          </Button>
        </Box>
      }
    >
      <Box gap="s24">
        <Box alignItems="flexStart" width="full" gap="s16">
          <MoneyField
            mandatory
            label={i18n.t('software_management.software_setups.create_payment_modal.amount')}
            field={form.get('amount')}
            currency={form.get('currency').value}
            placeholder="0"
          />
          <CurrencySelector
            field={form.get('currency')}
            label={i18n.t('software_management.software_setups.create_payment_modal.currency')}
            filteredCurrencies={allowedCurrencies}
          />
          <DayPicker
            mandatory
            field={form.get('date')}
            label={i18n.t('software_management.software_setups.create_payment_modal.date')}
            maxDate={moment().endOf('day')}
          />
        </Box>
      </Box>
    </Modal>
  )
}

export default EditPaymentModal
